import React, { ReactNode, useCallback, useMemo, useRef, useState } from "react";

import {
    FormFieldErrorContext,
    FormValidationContext,
    RegisterFieldValidation,
    RegisteredFieldValidation,
    resolveFieldValue,
} from "../core/formValidation";

export interface FormProps {
    onSubmit: (event: SubmitEvent) => void | Promise<void>;
    children: ReactNode;
    className?: string;
    invalidMessage?: string;
}

const clearValidity = (form: HTMLFormElement, fieldName: string): void => {
    const controls = form.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >(`[name="${fieldName}"]`);

    controls.forEach((control) => {
        control.setCustomValidity("");
    });
};

const setFieldValidity = (
    form: HTMLFormElement,
    fieldName: string,
    message: string
): void => {
    const controls = form.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >(`[name="${fieldName}"]`);

    controls.forEach((control) => {
        control.setCustomValidity(message);
    });
};

export const Form: React.FC<FormProps> = ({
    onSubmit,
    children,
    className = "wvcb-form wpcf7 wpcf7-form",
    invalidMessage = "Ein oder mehrere Felder sind fehlerhaft. Bitte pruefen und erneut versuchen.",
}) => {
    const fieldValidationsRef = useRef<
        Map<string, RegisteredFieldValidation>
    >(new Map());

    const registerFieldValidation = useCallback<RegisterFieldValidation>(
        (validation) => {
            fieldValidationsRef.current.set(validation.name, validation);

            return () => {
                fieldValidationsRef.current.delete(validation.name);
            };
        },
        []
    );

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const clearFieldError = useCallback((name: string): void => {
        setFieldErrors((previousErrors) => {
            if (!previousErrors[name]) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors[name];
            return nextErrors;
        });
    }, []);

    const getFieldError = useCallback(
        (name: string): string | null => fieldErrors[name] ?? null,
        [fieldErrors]
    );

    const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
        event
    ) => {
        const form = event.currentTarget;
        const formData = new FormData(form);
        const nextErrors: Record<string, string> = {};

        fieldValidationsRef.current.forEach((validation) => {
            clearValidity(form, validation.name);

            const value = resolveFieldValue(formData, validation.name);
            const fieldIsValid = validation.validate(value, formData);

            if (!fieldIsValid) {
                const message =
                    validation.message ?? "Bitte Eingabe pruefen.";

                nextErrors[validation.name] = message;
                setFieldValidity(
                    form,
                    validation.name,
                    message
                );
            }
        });

        setFieldErrors(nextErrors);

        const isValid = Object.keys(nextErrors).length === 0;

        if (!isValid) {
            event.preventDefault();
            return;
        }

        await onSubmit(event.nativeEvent as SubmitEvent);
    };

    const contextValue = useMemo(
        () => ({
            registerFieldValidation,
            getFieldError,
            clearFieldError,
        }),
        [clearFieldError, getFieldError, registerFieldValidation]
    );

    const hasErrors = Object.keys(fieldErrors).length > 0;

    return (
        <FormValidationContext.Provider value={registerFieldValidation}>
            <FormFieldErrorContext.Provider value={contextValue}>
                <form
                    className={className}
                    onSubmit={handleSubmit}
                    noValidate
                >
                    {children}

                    <div
                        className="wpcf7-response-output"
                        role="alert"
                        aria-hidden={!hasErrors}
                    >
                        {hasErrors ? invalidMessage : ""}
                    </div>
                </form>
            </FormFieldErrorContext.Provider>
        </FormValidationContext.Provider>
    );
};

